import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { useLazyValidatePromoCodeQuery } from '../../services/orderApi';
import type { Promocode } from '../../../../types/order.types';
import { isPromoActive, computePromoDiscount } from '../utils/promoDiscount';
import { useSelectedOrgName } from '@/hooks/useEntityScope';

interface UsePromoCodeOptions {
  subTotalAmount: number;
  totalAmount: number;
  isCartOrder: boolean;
  finalPaymentAmount?: (amount: number) => void;
  setTotalAmount: (v: number) => void;
  setSavingsAmount: (v: number) => void;
}

interface AppliedPromoDetails {
  code: string;
  discountType: string;
  discountValue: number;
}

export function usePromoCode(opts: UsePromoCodeOptions) {
  const dispatch = useDispatch();
  const location = useLocation();
  const selectedOrgName = useSelectedOrgName() ?? '';
  const [triggerValidatePromo] = useLazyValidatePromoCodeQuery();

  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [isPromocodeInvalid, setIsPromocodeInvalid] = useState(false);
  const [promocodeMessage, setPromocodeMessage] = useState('');
  const [isPromoApplied, setIsPromoApplied] = useState(false);
  const [promoCodeId, setPromoCodeId] = useState<string>();
  const [promoCodeIdForOrderCart, setPromoCodeIdForOrderCart] = useState<string | null>(null);
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [appliedPromoDetails, setAppliedPromoDetails] =
    useState<AppliedPromoDetails | null>(null);
  // Captured at apply time so removal can restore the pre-discount total.
  const [preDiscountTotal, setPreDiscountTotal] = useState<number | null>(null);
  // Bumping this key force-remounts the input so its uncontrolled value clears.
  const [promoInputResetKey, setPromoInputResetKey] = useState(0);

  function calculateDiscountedAmount(promo: Promocode) {
    if (!isPromoActive(promo)) {
      setIsPromocodeInvalid(true);
      setPromocodeMessage('Promo code has expired');
      setPromoCodeLoading(false);
      return;
    }
    if (!promo.discountValue) { setPromoCodeLoading(false); return; }

    setIsPromocodeInvalid(false);
    setPromocodeMessage('');
    setIsPromoApplied(true);
    setAppliedPromoDetails({
      code: (promo.promocode || promoCode || '').toUpperCase(),
      discountType: promo.discountType,
      discountValue: promo.discountValue,
    });
    setPreDiscountTotal(opts.totalAmount);

    if (opts.isCartOrder && location?.state) {
      if (promo.promocodeId) setPromoCodeIdForOrderCart(promo.promocodeId);
    } else {
      setPromoCodeId(promo.promocodeId);
    }

    // Preserve the existing base-per-type choice: fixed discounts apply to the
    // subtotal, percentage discounts to the running total.
    // Note: the pre-refactor code reported savings as `subTotalAmount` when a
    // discount fully zeroed the total; the util clamps savings to `base`. These
    // differ only for a %-discount >= 100% when subtotal != total — unreachable
    // for real promos, and clamping to base is the more correct value.
    const base = promo.discountType === 'fixed_discount' ? opts.subTotalAmount : opts.totalAmount;
    const { savings, discountedTotal } = computePromoDiscount(promo, base);
    opts.setTotalAmount(discountedTotal);
    opts.finalPaymentAmount?.(discountedTotal);
    opts.setSavingsAmount(savings);

    setPromoCodeLoading(false);
  }

  const applyPromoCode = useCallback(async () => {
    if (opts.subTotalAmount <= 0 || !promoCode) {
      setIsPromocodeInvalid(true);
      setPromocodeMessage('Please enter promo code');
      return;
    }
    setPromoCodeLoading(true);
    try {
      const entity = selectedOrgName;
      const result = await triggerValidatePromo({ promocode: promoCode, entity }).unwrap();
      if (result && result.length > 0) calculateDiscountedAmount(result[0]);
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      if (error?.data?.message?.includes('Promocode not applicable')) {
        setIsPromocodeInvalid(true);
        setPromocodeMessage('Promo code has expired');
      } else if (error?.data?.message?.includes('Invalid Promocode')) {
        setIsPromocodeInvalid(true);
        setPromocodeMessage('Invalid promo code');
      } else {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to validate promo code' }));
      }
      setPromoCodeLoading(false);
    }
    // calculateDiscountedAmount / triggerValidatePromo / dispatch are stable
    // refs from RTK Query / Redux. The captured opts.* fields beyond
    // subTotalAmount are only used inside calculateDiscountedAmount, which
    // reads its own closure when called.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode, opts.subTotalAmount]);

  function removePromoCode() {
    const restoredTotal = preDiscountTotal ?? opts.subTotalAmount;
    setIsPromoApplied(false);
    setAppliedPromoDetails(null);
    setIsPromocodeInvalid(false);
    setPromocodeMessage('');
    setPromoCode(null);
    opts.setSavingsAmount(0);
    opts.setTotalAmount(restoredTotal);
    opts.finalPaymentAmount?.(restoredTotal);
    setPromoCodeId(undefined);
    setPromoCodeIdForOrderCart(null);
    setPreDiscountTotal(null);
    setPromoInputResetKey((k) => k + 1);
  }

  return {
    promoCode, setPromoCode,
    isPromocodeInvalid, promocodeMessage, isPromoApplied,
    promoCodeLoading, applyPromoCode, removePromoCode,
    promoCodeId, promoCodeIdForOrderCart,
    appliedPromoDetails, promoInputResetKey,
  };
}
